import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "./AuthContext";

const OrderContext = React.createContext();

export function useOrder() {
  return useContext(OrderContext);
}

export function OrderProvider({ children }) {
  const initialCart = {
    cartId: 0,
    products: [],
    status: 0,
    matched: false,
  };

  const [cartInfo, setCartInfo] = useState(initialCart);
  const [isFetching, setIsFetching] = useState(true);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const resetCart = () =>
    setCartInfo((oldVal) => ({
      ...initialCart,
      cartId: oldVal.cartId,
      status: 1,
    }));

  const clearCart = () => {
    setCartInfo((oldVal) => ({
      ...oldVal,
      products: [],
    }));
  };

  useEffect(() => {
    // const cart = JSON.parse(sessionStorage.getItem("cart"));
    // if (cart !== null) {
    //   setShoppingList(cart);
    //   setIsFetching(false);
    //   return;
    // }

    if (!currentUser) {
      setIsFetching(false);
      setCartInfo(initialCart);
      return;
    }

    fetch("https://api.redfoxcafe.ir/v1/client/active-cart/", {
      method: "GET",
      headers: {
        "Authorization-Token": "Bearer " + currentUser.access_token,
      },
    })
      .then((response) => response.json())
      .then((d) => {
        if (d.status === "200") {
          const newInfo = {
            cartId: d.data.CartId,
            products: d.data.CartDetails ? d.data.CartDetails : [],
            status: d.data.CartStatusNumber ? d.data.CartStatusNumber : 0,
            matched: true,
          };

          setCartInfo(newInfo);
          setIsFetching(false);
        } else if (d.status === "401") {
          toast.info(d.message);
          setIsFetching(false);
          logout();
          navigate("/login", { replace: true });
        } else {
          toast.error(d.message);
        }
      });
  }, [currentUser]);

  useEffect(() => {
    const writeData = async () => {
      //sessionStorage.setItem("cart", JSON.stringify(shoppingList));

      if (!currentUser) return;

      const data = cartInfo.products.map((val) => {
        if (val.ProductMultiMode === "true") {
          return {
            productId: val.ProductID,
            quantity: 1,
            multiMode: "true",
            multiModeInformation: val.ProductMultiModeDetails.map((option) => ({
              multiModeId: option.ProductMultiModeId,
              quantity: option.Quantity,
            })),
          };
        }

        return {
          productId: val.ProductID,
          quantity: val.Quantity,
          multiMode: "false",
        };
      });

      const dataToSend = {
        products: data.length !== 0 ? data : "null",
        cartId: cartInfo.cartId,
      };

      await fetch("https://api.redfoxcafe.ir/v1/client/update-cart/", {
        method: "POST",
        headers: {
          "Authorization-Token": "Bearer " + currentUser.access_token,
        },
        body: JSON.stringify(dataToSend),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === "200") {
            if (cartInfo.status !== data.data.CartStatusNumber) {
              setCartInfo((oldVal) => ({
                ...oldVal,
                products: data.data.CartDetails ? data.data.CartDetails : [],
                status: data.data.CartStatusNumber,
                matched: true,
              }));
            } else {
              setCartInfo((oldVal) => ({
                ...oldVal,
                matched: true,
              }));
            }
            return;
          } else if (data.status === "401") {
            toast.info(data.message);
            logout();
            navigate("/login", { replace: true });
          } else if (data.status === "416") {
            toast.error(data.message);
            setCartInfo((oldVal) => ({
              ...oldVal,
              products: data.data.CartDetails ? data.data.CartDetails : [],
              matched: true,
            }));
          } else {
            toast.error(data.message);
          }
        });
    };
    if (cartInfo.matched) return;
    if (isInProcess()) return;
    if (!isFetching) {
      writeData();
    }
  }, [cartInfo.matched]);

  // function decideOnStatus(status) {

  // }

  function isInProcess() {
    const status = Number(cartInfo.status);
    if (status > 3 && status < 7) {
      return true;
    }
    return false;
  }

  function addToCart(item) {
    if (isInProcess()) return;

    let alreadyExisted = false;

    const newList = cartInfo.products.map((val) => {
      if (val.ProductID === item.ProductID) {
        alreadyExisted = true;
        return item;
      }

      return val;
    });

    if (alreadyExisted) {
      setCartInfo((oldInfo) => ({
        ...oldInfo,
        products: newList,
        matched: false,
      }));
    } else {
      setCartInfo((oldInfo) => ({
        ...oldInfo,
        products: [...newList, item],
        matched: false,
      }));
    }
  }

  function isInCart(id) {
    for (let i = 0; i < cartInfo.products.length; i++) {
      if (cartInfo.products[i].ProductID === id)
        return [
          true,
          cartInfo.products[i].Quantity,
          cartInfo.products[i].ProductMultiMode
            ? cartInfo.products[i].ProductMultiModeDetails
            : [],
        ];
    }
    return [false, 0, []];
  }

  function removeFromCart(id, mId) {
    if (isInProcess()) return;
    if (mId) {
      const newList = cartInfo.products.map((val) => {
        if (val.ProductID === id) {
          const newOptions = val.ProductMultiModeDetails.filter(
            (option) => option.ProductMultiModeId !== mId
          );

          const [price, priceAfterDiscounts] = getMultiTotal(newOptions);

          return {
            ...val,
            ProductMultiModeDetails: newOptions,
            Price: price,
            PriceAfterDiscount: priceAfterDiscounts,
          };
        }

        return val;
      });

      const filteredList = newList.filter(
        (val) =>
          !(val.ProductMultiMode === "true") ||
          val.ProductMultiModeDetails.length !== 0
      );

      setCartInfo((oldInfo) => ({
        ...oldInfo,
        products: filteredList,
        matched: false,
      }));
    } else {
      setCartInfo((oldInfo) => ({
        ...oldInfo,
        products: oldInfo.products.filter((val) => val.ProductID !== id),
        matched: false,
      }));
    }
  }

  function getAllPrices() {
    let price = 0;
    let discounts = 0;
    let priceAfterDiscounts = 0;

    for (let i = 0; i < cartInfo.products.length; i++) {
      price += parseInt(cartInfo.products[i].Price);
      priceAfterDiscounts += parseInt(cartInfo.products[i].PriceAfterDiscount);
    }
    discounts = price - priceAfterDiscounts;

    return { price, discounts, priceAfterDiscounts };
  }

  function increaseQuantity(id, mId) {
    if (isInProcess()) return;

    setCartInfo((oldInfo) => ({
      ...oldInfo,
      matched: false,
      products: oldInfo.products.map((val) => {
        if (val.ProductID === id) {
          if (mId) {
            const newOptions = val.ProductMultiModeDetails.map((option) => {
              if (option.ProductMultiModeId === mId) {
                return {
                  ...option,
                  Quantity: option.Quantity + 1,
                  Price:
                    (option.Price * (option.Quantity + 1)) / option.Quantity,
                  PriceAfterDiscount:
                    (option.PriceAfterDiscount * (option.Quantity + 1)) /
                    option.Quantity,
                };
              }

              return option;
            });

            const [price, priceAfterDiscounts] = getMultiTotal(newOptions);

            return {
              ...val,
              Price: price,
              PriceAfterDiscount: priceAfterDiscounts,
              ProductMultiModeDetails: newOptions,
            };
          }
          return {
            ...val,
            Quantity: val.Quantity + 1,
            Price: (val.Price * (val.Quantity + 1)) / val.Quantity,
            PriceAfterDiscount:
              (val.PriceAfterDiscount * (val.Quantity + 1)) / val.Quantity,
          };
        }

        return val;
      }),
    }));
  }

  function decreaseQuantity(id, mId) {
    if (isInProcess()) return;

    setCartInfo((oldInfo) => ({
      ...oldInfo,
      matched: false,
      products: oldInfo.products.map((val) => {
        if (val.ProductID === id) {
          if (mId) {
            const newOptions = val.ProductMultiModeDetails.map((option) => {
              if (option.ProductMultiModeId === mId) {
                if (option.Quantity === 1) return option;
                return {
                  ...option,
                  Quantity: option.Quantity - 1,
                  Price:
                    (option.Price * (option.Quantity - 1)) / option.Quantity,
                  PriceAfterDiscount:
                    (option.PriceAfterDiscount * (option.Quantity - 1)) /
                    option.Quantity,
                };
              }

              return option;
            });

            const [price, priceAfterDiscounts] = getMultiTotal(newOptions);

            return {
              ...val,
              Price: price,
              PriceAfterDiscount: priceAfterDiscounts,
              ProductMultiModeDetails: newOptions,
            };
          }
          if (val.Quantity === 1) return val;
          return {
            ...val,
            Quantity: val.Quantity - 1,
            Price: (val.Price * (val.Quantity - 1)) / val.Quantity,
            PriceAfterDiscount:
              (val.PriceAfterDiscount * (val.Quantity - 1)) / val.Quantity,
          };
        }

        return val;
      }),
    }));
  }

  const dropZeros = (price) => Number(price) / 1000;

  const getLength = () => cartInfo.products.length;

  const isInFetch = () => isFetching;

  const shoppingList = cartInfo.products;

  const cartId = cartInfo.cartId;

  const status = cartInfo.status;

  const setStatus = (number) => {
    setCartInfo((oldVal) => ({
      ...oldVal,
      status: number,
    }));
  };

  const paymentIsPending = status === 3;

  function getMultiTotal(options) {
    let price = 0;
    let priceAfterDiscounts = 0;
    for (let i = 0; i < options.length; i++) {
      price += parseInt(options[i].Price);
      priceAfterDiscounts += parseInt(options[i].PriceAfterDiscount);
    }
    return [price, priceAfterDiscounts];
  }

  const info = {
    shoppingList,
    addToCart,
    removeFromCart,
    getAllPrices,
    dropZeros,
    isInCart,
    increaseQuantity,
    decreaseQuantity,
    getLength,
    getMultiTotal,
    cartId,
    isInProcess,
    isInFetch,
    setCartInfo,
    status,
    resetCart,
    paymentIsPending,
    setStatus,
    clearCart,
    setCartInfo,
  };

  return <OrderContext.Provider value={info}>{children}</OrderContext.Provider>;
}
